import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FISHIPEDIA_RETURN_CONTEXT_KEY,
  resolveFishipediaCloseNavigation,
} from "../app/fishipediaReturnContext";
import {
  bindFishipediaHistoryEntry,
  dispatchFishipediaCloseAction,
} from "../app/fishipediaRestoreHistory";
import fishipediaManifest from "../data/fishipediaManifest.json";
import DesktopRightRail from "../components/DesktopRightRail";
import "../styles/pages/Fishipedia.css";

const FISHIPEDIA_FIRST_SLUG = fishipediaManifest[0]?.slug ?? null;

function wrapReferencesSections(markdown) {
  return markdown.replace(
    /(##[^\n]*(?:Key References|Основни източници)[\s\S]*?)(?=\n<a id="|\n##\s|$)/g,
    (block) => {
      const lines = block.split("\n");
      const headerIndex = lines.findIndex((line) => line.trim().startsWith("##"));

      if (headerIndex === -1) {
        return block;
      }

      const headerLine = lines[headerIndex];
      const afterHeader = lines.slice(headerIndex + 1).join("\n");

      return `${headerLine}\n\n<div class="fishipedia-references">\n${afterHeader}\n</div>\n`;
    }
  );
}

function getLanguageCode(language) {
  return (language || "en").toLowerCase().split("-")[0];
}

function normalizeHashValue(value) {
  return (value || "")
    .replace(/^#/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function normalizeSearchValue(value) {
  return (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function resolveFishipediaSlug(hashValue) {
  const normalized = normalizeHashValue(hashValue);

  if (!normalized) {
    return null;
  }

  const directMatch = fishipediaManifest.find((entry) => entry.slug === normalized);

  if (directMatch) {
    return directMatch.slug;
  }

  const anchorMatch = fishipediaManifest.find(
    (entry) => entry.anchorId && entry.anchorId === normalized
  );

  return anchorMatch ? anchorMatch.slug : null;
}

function buildSearchText(entry) {
  return normalizeSearchValue(
    [entry.label, entry.slug.replace(/-/g, " "), entry.anchorId || ""].join(" ")
  );
}

function scrollToSpecies(hashValue, slug) {
  const hashId = normalizeHashValue(hashValue);
  const directTarget = hashId ? document.getElementById(hashId) : null;
  const fallbackTarget = slug
    ? document.getElementById(`fishipedia-entry-${slug}`)
    : null;
  const target = directTarget || fallbackTarget;

  if (!target) {
    return;
  }

  target.scrollIntoView({ behavior: "smooth", block: "start" });
  target.style.transition = "background-color 0.8s";
  target.style.backgroundColor = "rgba(255,255,0,0.3)";
  window.setTimeout(() => {
    target.style.backgroundColor = "transparent";
  }, 1200);
}

function getFishipediaImageState(image) {
  const explicitState = image.dataset.fishipediaImageState;
  if (explicitState === "ready" || explicitState === "error") {
    return explicitState;
  }

  if (image.complete) {
    return image.naturalWidth > 0 ? "ready" : "error";
  }

  return "pending";
}

function isVisibleFishipediaImage(image) {
  return getFishipediaImageState(image) !== "error";
}

function updateFishipediaImageGrid(grid) {
  const mainContainer = grid.querySelector(".main-image");
  const sideContainer = grid.querySelector(".side-images");

  const mainImages = mainContainer
    ? Array.from(mainContainer.querySelectorAll("img"))
    : [];
  const sideImages = sideContainer
    ? Array.from(sideContainer.querySelectorAll("img"))
    : [];

  const visibleMainImages = mainImages.filter(isVisibleFishipediaImage);
  const visibleSideImages = sideImages.filter(isVisibleFishipediaImage);
  const visibleImages = [...visibleMainImages, ...visibleSideImages];

  mainImages.forEach((image) => {
    image.hidden = !isVisibleFishipediaImage(image);
  });

  sideImages.forEach((image) => {
    image.hidden = !isVisibleFishipediaImage(image);
  });

  if (mainContainer) {
    mainContainer.hidden = visibleMainImages.length === 0;
  }

  if (sideContainer) {
    sideContainer.hidden = visibleSideImages.length === 0;
    sideContainer.dataset.visibleCount = String(visibleSideImages.length);
  }

  grid.hidden = visibleImages.length === 0;
  grid.dataset.visibleCount = String(visibleImages.length);
  grid.dataset.mainVisible = visibleMainImages.length > 0 ? "true" : "false";
}

export default function Fishipedia({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sentinelRef = useRef(null);
  const markdownCacheRef = useRef(new Map());
  const loadRequestRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);

  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [fsOpen, setFsOpen] = useState(false);
  const [fsImages, setFsImages] = useState([]);
  const [fsIndex, setFsIndex] = useState(0);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const languageCode = getLanguageCode(i18n.language);

  useLayoutEffect(() => {
    bindFishipediaHistoryEntry(
      location.state?.[FISHIPEDIA_RETURN_CONTEXT_KEY],
      location
    );
  }, [location]);

  const closeUtilityPage = useCallback(() => {
    const closeAction = resolveFishipediaCloseNavigation(
      location.state,
      location.key
    );
    dispatchFishipediaCloseAction({ navigate, location, closeAction });
  }, [location, navigate]);

  const fetchSpeciesMarkdown = useCallback(async (slug) => {
    const cacheKey = `${languageCode}:${slug}`;

    if (markdownCacheRef.current.has(cacheKey)) {
      return markdownCacheRef.current.get(cacheKey);
    }

    const languagesToTry = languageCode === "en" ? ["en"] : [languageCode, "en"];

    for (const language of languagesToTry) {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/Markdown/fishipedia/${slug}/${language}.md`
      );

      if (!response.ok) {
        continue;
      }

      const markdown = await response.text();

      if (!markdown.trim()) {
        continue;
      }

      const wrapped = wrapReferencesSections(markdown);
      markdownCacheRef.current.set(cacheKey, wrapped);
      return wrapped;
    }

    throw new Error(`Unable to load Fishipedia entry for ${slug}.`);
  }, [languageCode]);

  const loadInitialSpecies = useCallback(async (slug) => {
    if (!slug) {
      setEntries([]);
      setIsInitialLoading(false);
      return;
    }

    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    loadingMoreRef.current = false;
    setIsInitialLoading(true);
    setIsLoadingMore(false);
    setLoadError("");
    setEntries([]);

    try {
      const markdown = await fetchSpeciesMarkdown(slug);

      if (loadRequestRef.current !== requestId) {
        return;
      }

      setEntries([{ slug, markdown }]);
    } catch (error) {
      if (loadRequestRef.current === requestId) {
        setLoadError("Unable to load Fishipedia.");
      }
    } finally {
      if (loadRequestRef.current === requestId) {
        setIsInitialLoading(false);
      }
    }
  }, [fetchSpeciesMarkdown]);

  const loadNextSpecies = useCallback(async () => {
    if (loadingMoreRef.current || isInitialLoading || entries.length === 0) {
      return;
    }

    const lastSlug = entries[entries.length - 1].slug;
    const lastIndex = fishipediaManifest.findIndex((entry) => entry.slug === lastSlug);
    const nextEntry = fishipediaManifest[lastIndex + 1];

    if (!nextEntry || entries.some((entry) => entry.slug === nextEntry.slug)) {
      return;
    }

    const requestId = loadRequestRef.current;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const markdown = await fetchSpeciesMarkdown(nextEntry.slug);

      if (loadRequestRef.current !== requestId) {
        return;
      }

      setEntries((currentEntries) => {
        if (currentEntries.some((entry) => entry.slug === nextEntry.slug)) {
          return currentEntries;
        }

        return [...currentEntries, { slug: nextEntry.slug, markdown }];
      });
    } finally {
      if (loadRequestRef.current === requestId) {
        setIsLoadingMore(false);
      }

      loadingMoreRef.current = false;
    }
  }, [entries, fetchSpeciesMarkdown, isInitialLoading]);

  function handleSearch(event) {
    const value = event.target.value;
    const normalizedQuery = normalizeSearchValue(value);

    setSearch(value);

    if (!normalizedQuery) {
      setSuggestions([]);
      return;
    }

    const nextSuggestions = fishipediaManifest.filter((entry) =>
      buildSearchText(entry).includes(normalizedQuery)
    );

    setSuggestions(nextSuggestions);
  }

  function openSpecies(slug) {
    if (!slug) {
      return;
    }

    setSearch("");
    setSuggestions([]);

    const nextHash = `#${slug}`;

    if (location.hash === nextHash) {
      loadInitialSpecies(slug);
      return;
    }

    navigate({ pathname: location.pathname, hash: nextHash });
  }

  useEffect(() => {
    const initialSlug = resolveFishipediaSlug(location.hash) || FISHIPEDIA_FIRST_SLUG;
    loadInitialSpecies(initialSlug);
  }, [languageCode, loadInitialSpecies, location.hash]);

  useEffect(() => {
    if (!location.hash || entries.length === 0) {
      return undefined;
    }

    const resolvedSlug = resolveFishipediaSlug(location.hash) || entries[0].slug;
    const timer = window.setTimeout(() => {
      scrollToSpecies(location.hash, resolvedSlug);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [location.hash, entries]);

  useEffect(() => {
    if (entries.length === 0) {
      return undefined;
    }

    const cleanups = [];
    const grids = document.querySelectorAll(".fishipedia-image-grid");

    grids.forEach((grid) => {
      const images = Array.from(grid.querySelectorAll("img"));

      images.forEach((image, index) => {
        const syncImageState = () => {
          image.dataset.fishipediaImageState =
            image.naturalWidth > 0 ? "ready" : "error";
          updateFishipediaImageGrid(grid);
        };

        const handleImageClick = () => {
          const visibleImages = images.filter(isVisibleFishipediaImage);
          const visibleSources = visibleImages
            .map((visibleImage) => visibleImage.getAttribute("src"))
            .filter(Boolean);
          const visibleIndex = visibleImages.indexOf(image);

          if (visibleIndex === -1 || visibleSources.length === 0) {
            return;
          }

          setFsImages(visibleSources);
          setFsIndex(visibleIndex);
          setFsOpen(true);
        };

        image.dataset.fishipediaImageState = image.complete
          ? image.naturalWidth > 0
            ? "ready"
            : "error"
          : "pending";

        image.addEventListener("load", syncImageState);
        image.addEventListener("error", syncImageState);
        image.addEventListener("click", handleImageClick);
        cleanups.push(() => image.removeEventListener("load", syncImageState));
        cleanups.push(() => image.removeEventListener("error", syncImageState));
        cleanups.push(() => image.removeEventListener("click", handleImageClick));
      });

      updateFishipediaImageGrid(grid);
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [entries]);

  useEffect(() => {
    if (!sentinelRef.current || entries.length === 0 || isInitialLoading) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries.some((entry) => entry.isIntersecting)) {
          loadNextSpecies();
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [entries, isInitialLoading, loadNextSpecies]);

  const fsPrev = () => {
    setFsIndex((index) => Math.max(0, index - 1));
  };

  const fsNext = () => {
    setFsIndex((index) => Math.min(fsImages.length - 1, index + 1));
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`fishipedia-shell${
        enableDesktopRightRail ? " fishipedia-shell--with-right-rail" : ""
      }`}
    >
      <div className="fishipedia-page">
      <button
        type="button"
        className="fishipedia-page-close"
        onClick={closeUtilityPage}
        aria-label={t("common_close")}
      >
        <X size={18} strokeWidth={2.5} aria-hidden="true" />
      </button>
      <h1>{t("fishipedia_title")}</h1>
      <p className="fishipedia-intro">{t("fishipedia_intro")}</p>

      <div className="fishipedia-search">
        <input
          type="text"
          className="lesson-search"
          placeholder={t("fishipedia_search_placeholder")}
          value={search}
          onChange={handleSearch}
          onKeyDown={(event) => {
            if (event.key === "Enter" && suggestions.length > 0) {
              event.preventDefault();
              openSpecies(suggestions[0].slug);
            }
          }}
        />

        {suggestions.length > 0 && (
          <div className="autocomplete-box">
            {suggestions.map((entry) => (
              <div
                key={entry.slug}
                className="autocomplete-item"
                onClick={() => openSpecies(entry.slug)}
              >
                {entry.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {entries.length > 0 ? (
        <>
          {entries.map((entry) => (
            <section
              key={entry.slug}
              id={`fishipedia-entry-${entry.slug}`}
              data-fishipedia-slug={entry.slug}
            >
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {entry.markdown}
              </ReactMarkdown>
            </section>
          ))}

          <div ref={sentinelRef} aria-hidden="true" />

          {isLoadingMore && <p>{t("fishipedia_loading")}</p>}
        </>
      ) : isInitialLoading ? (
        <p>{t("fishipedia_loading")}</p>
      ) : (
        <p>{loadError}</p>
      )}

      </div>

      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}

      {fsOpen && (
        <div className="fishipedia-fs-overlay" onClick={() => setFsOpen(false)}>
          <div
            className="fishipedia-fs-inner"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={fsImages[fsIndex]}
              alt=""
              className="fishipedia-fs-image"
            />

            {fsIndex > 0 && (
              <button className="fishipedia-fs-arrow left" onClick={fsPrev}>
                {"\u2039"}
              </button>
            )}

            {fsIndex < fsImages.length - 1 && (
              <button className="fishipedia-fs-arrow right" onClick={fsNext}>
                {"\u203A"}
              </button>
            )}

            <button
              className="fishipedia-fs-close"
              onClick={() => setFsOpen(false)}
            >
              {"\u2715"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
